import Bugsnag from "@bugsnag/js";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios, { AxiosError, AxiosProgressEvent, AxiosResponse } from "axios";
import type {
  DeleteApiResponse,
  UploadApiErrorResponse,
  UploadApiResponse,
} from "cloudinary";
import { env } from "next-runtime-env";

import { getResizedCropperImage } from "components/photoCropper/PhotoCropper";
import { UploadedPhoto } from "types/schema";
import {
  CLOUDINARY_CONFIGURATION_ERROR,
  FAILED_SIGNATURE_ERROR,
} from "utils/error-utils";
import { getDefaultCropForPhoto } from "utils/photo-utils";

import {
  ModerationResults,
  RekModerationResult,
  SignatureResponse,
  UploadedPhotoModerationRejection,
} from "../../types/queries";

/**
 * Photo query keys.
 *
 * These uniquely identify queries in the `react-query` cache.
 *
 * [More on this pattern](https://tkdodo.eu/blog/effective-react-query-keys)
 */
export const photoKeys = {
  all: [{ scope: "photos" }] as const,
  upload: (slot: string) =>
    [
      {
        scope: "photos",
        action: "upload",
        slot,
      },
    ] as const,
};

export type UploadPhotoArgs = {
  aspectRatio: string;
  file: File;
  museSlug: string;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  preset: "ml_default" | "moderated";
  signal?: AbortSignal;
};

async function uploadPhoto({
  aspectRatio,
  file,
  museSlug,
  onUploadProgress,
  preset,
  signal,
}: UploadPhotoArgs): Promise<UploadedPhoto> {
  const cloudinaryApiKey = env("NEXT_PUBLIC_CLOUDINARY_API_KEY");

  if (!cloudinaryApiKey || !env("NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME")) {
    throw CLOUDINARY_CONFIGURATION_ERROR;
  }

  let signature;
  try {
    const response = await axios.get<SignatureResponse>(
      "/_canvas/api/photos/signature?slug=" + museSlug
    );
    signature = response.data;
  } catch (error) {
    const loggableError =
      error instanceof Error ? error : FAILED_SIGNATURE_ERROR;

    Bugsnag.notify(loggableError, (event) => {
      event.severity = "error";
      event.unhandled = true;
    });

    throw loggableError;
  }

  const formData = new FormData();

  formData.append("file", file);
  formData.append("api_key", cloudinaryApiKey);
  formData.append("timestamp", signature.timestamp);
  formData.append("signature", signature.signature);
  formData.append("folder", museSlug);
  formData.append("upload_preset", preset);

  const response: AxiosResponse<UploadApiResponse> = await axios.post(
    "https://api.cloudinary.com/v1_1/" +
      env("NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME") +
      "/image/upload",
    formData,
    {
      timeout: 0,
      // ^ Remove the default 5s timeout for uploads
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
      signal,
    }
  );

  /**
   * Throw an error if photo is rejected by AWS Rekognition.
   *
   * Reason for rejection could be added by using the moderation response
   */
  const moderationResult =
    response.data.moderation &&
    ((response.data.moderation as any as ModerationResults).find(
      (m) => m.kind === "aws_rek"
    ) as RekModerationResult);

  if (moderationResult && moderationResult.status === "rejected") {
    throw {
      name: "rejected",
      reasons: moderationResult.response.moderation_labels,
    } as UploadedPhotoModerationRejection;
  }

  // Preload the image here so cropper can show immediately
  const preloadedImg = new Image();
  preloadedImg.src = getResizedCropperImage(response.data.public_id).toURL();
  await preloadedImg.decode();
  // ^ This essentially acts as a promise version of `onload`

  return {
    public_id: response.data.public_id,
    width: response.data.width,
    height: response.data.height,
    crop: getDefaultCropForPhoto({ photo: response.data, aspectRatio }),
    filter: "none",
  };
}

export function useUploadPhoto(
  slot: string,
  options: UseMutationOptions<
    UploadedPhoto,
    UploadApiErrorResponse | UploadedPhotoModerationRejection,
    UploadPhotoArgs
  >
) {
  return useMutation(photoKeys.upload(slot), uploadPhoto, options);
}

type DeletePhotoArgs = {
  photoId: string;
};

async function deletePhoto({ photoId }: DeletePhotoArgs) {
  const response: AxiosResponse<DeleteApiResponse> = await axios.delete(
    "/_canvas/api/photos/" + encodeURIComponent(photoId)
  );

  return response.data;
}

export function useDeletePhoto(
  options: UseMutationOptions<DeleteApiResponse, AxiosError, DeletePhotoArgs>
) {
  return useMutation([], deletePhoto, options);
}
